import React, { useMemo, useState } from 'react';
import { api } from '../api';
import { computeSOWCosts } from '../computeSOWCosts';
import SOWCostTable from './SOWCostTable';

const SELECT = ({ value, onChange, options }) => (
  <select value={value || ''} onChange={e => onChange(e.target.value)}>
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

function Field({ label, children }) {
  return (
    <div style={{marginBottom:10}}>
      <label style={{fontSize:11,fontWeight:600,color:'var(--text-muted)',display:'block',marginBottom:3}}>{label}</label>
      {children}
    </div>
  );
}

function NumberInput({ value, onChange, placeholder }) {
  return (
    <input type="number" defaultValue={value ?? ''} placeholder={placeholder || '0'}
      onBlur={e => onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
      style={{ width: '100%' }} />
  );
}

function CheckRow({ label, checked, onChange }) {
  return (
    <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:13,marginBottom:8}}>
      <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked ? 1 : 0)}
        style={{width:15,height:15,accentColor:'var(--brand)'}} />
      {label}
    </label>
  );
}

export default function DemoSection({ deal, onUpdate, status, statusLabel, statusColor }) {
  const [data, setData] = useState(deal.sow_demo || {});
  const [open, setOpen] = useState(false);

  const costLines = useMemo(() => {
    const all = computeSOWCosts({ ...deal, sow_demo: data });
    return all.filter(l => l.section === 'demo');
  }, [deal, data]);

  const save = async (field, value) => {
    const updated = { ...data, [field]: value };
    setData(updated);
    await api.updateSowDemo(deal.id, { [field]: value });
  };

  const dumpsterCost = (data.dumpster_count || 0) * (data.dumpster_size === '10yd' ? 350 : data.dumpster_size === '15yd' ? 425 : data.dumpster_size === '30yd' ? 650 : 500);
  const hasSomething = data.dumpster_count || data.full_cleanout || data.haul_away || data.demo_labor;

  return (
    <div className="card" style={{marginBottom:16,padding:0,overflow:'hidden'}}>
      <div style={{display:'flex',alignItems:'center',gap:10,padding:'12px 16px',cursor:'pointer',
        borderLeft:'4px solid #e67e22'}} onClick={() => setOpen(o => !o)}>
        <div style={{flex:1}}>
          <div style={{fontWeight:600,fontSize:14}}>Demo & Disposal</div>
          <div style={{fontSize:12,color:'var(--text-muted)',marginTop:2}}>
            {hasSomething
              ? [data.dumpster_count && `${data.dumpster_count}× dumpster`, data.full_cleanout && 'Full cleanout', data.haul_away && 'Haul away', data.demo_labor && `$${data.demo_labor?.toLocaleString()} labor`].filter(Boolean).join(' · ')
              : 'Tap to fill in demo & disposal scope'}
          </div>
        </div>
        {statusLabel && (
          <span style={{fontSize:11,fontWeight:600,padding:'2px 8px',borderRadius:12,
            background:`${statusColor}22`,color:statusColor,whiteSpace:'nowrap'}}>
            {statusLabel}
          </span>
        )}
        <span style={{color:'var(--text-muted)',fontSize:16}}>{open ? '▲' : '▼'}</span>
      </div>

      {open && (
        <div style={{padding:'16px',borderTop:'1px solid var(--bg)'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 20px'}}>

            <Field label="Dumpster Count">
              <NumberInput value={data.dumpster_count} onChange={v => save('dumpster_count', v)} placeholder="0" />
            </Field>
            <Field label="Dumpster Size">
              <SELECT value={data.dumpster_size} onChange={v => save('dumpster_size', v)}
                options={[['10yd','10 yd'],['15yd','15 yd'],['20yd','20 yd'],['30yd','30 yd']]} />
            </Field>

            <div style={{gridColumn:'span 2'}}>
              <CheckRow label="Full house cleanout" checked={data.full_cleanout} onChange={v => save('full_cleanout', v)} />
              <CheckRow label="Haul-away / junk removal" checked={data.haul_away} onChange={v => save('haul_away', v)} />
            </div>

            <Field label="Demo Labor Budget ($)">
              <NumberInput value={data.demo_labor} onChange={v => save('demo_labor', v)} placeholder="0" />
            </Field>

            {data.dumpster_count > 0 && (
              <div style={{display:'flex',alignItems:'flex-end',paddingBottom:10}}>
                <div style={{fontSize:12,color:'var(--text-muted)'}}>
                  Est. dumpster cost: <strong style={{color:'var(--text)'}}>${dumpsterCost.toLocaleString()}</strong>
                </div>
              </div>
            )}

            <div style={{gridColumn:'span 2',marginTop:4}}>
              <Field label="Notes">
                <textarea rows={2} defaultValue={data.notes || ''}
                  onBlur={e => save('notes', e.target.value)}
                  style={{resize:'vertical',width:'100%'}} placeholder="Specific demo scope, hazmat concerns…" />
              </Field>
            </div>

          </div>

          <SOWCostTable dealId={deal.id} lines={costLines} sowCosts={deal.sow_costs} onSynced={onUpdate} />
        </div>
      )}
    </div>
  );
}
